/**
 * Calendar config — single source of truth for the calendar view.
 *
 * V5.1.1 UI Module: ui.freshcards.module.calendar-config
 * Owns: zoom level, smart defaults, filter helpers, item-date resolution.
 * Used by: CalendarView, all 3 layouts (Month / Agenda / Columns).
 */

export type ZoomLevel = 'day' | '3-day' | 'week' | 'month' | 'quarter' | 'year';
export type TimeField = 'due' | 'created' | 'completed' | 'date' | 'updated';
export type CalendarLayout = 'month' | 'agenda' | 'columns';
export type QuickFilter = 'all' | 'overdue' | 'today' | 'this-week' | 'no-date';
export type StatusFilter = 'all' | 'done' | 'wip' | 'backlog' | 'review' | 'archived';
export type SortKey = 'date-asc' | 'date-desc' | 'title-asc' | 'title-desc' | 'status' | 'priority';
export type WeekStart = 'sunday' | 'monday';
/** A property filter is a map of propertyId → selected option ids. Empty
 *  array (or missing key) means "no filter for this property". */
export type PropertyFilter = Record<string, string[]>;

export const ZOOM_LABELS: Record<ZoomLevel, string> = {
  day: 'Day',
  '3-day': '3 days',
  week: 'Week',
  month: 'Month',
  quarter: 'Quarter',
  year: 'Year',
};

export const ZOOM_ORDER: ZoomLevel[] = ['day', '3-day', 'week', 'month', 'quarter', 'year'];

/** Smart default zoom for each time field. */
export const SMART_DEFAULT_ZOOM: Record<TimeField, ZoomLevel> = {
  due: 'month',        // planning is best seen monthly
  date: 'month',       // custom date, same as due
  created: 'week',     // intake is best seen weekly
  completed: '3-day',  // recent wins, frequent updates
  updated: 'week',     // recent activity is best seen weekly
};

export interface CalendarViewConfig {
  timeField: TimeField;
  layout: CalendarLayout;
  zoom: ZoomLevel;
  quickFilter: QuickFilter;
  statusFilter: StatusFilter;
  sort: SortKey;
  weekStart: WeekStart;
  hideWeekends: boolean;
  /** Per-(database) map of propertyId → selected option ids. AND across properties. */
  propertyFilter: PropertyFilter;
}

export const DEFAULT_CALENDAR_CONFIG: CalendarViewConfig = {
  timeField: 'due',
  layout: 'month',
  zoom: SMART_DEFAULT_ZOOM.due,
  quickFilter: 'all',
  statusFilter: 'all',
  sort: 'date-asc',
  weekStart: 'monday',
  hideWeekends: false,
  propertyFilter: {},
};

export const CALENDAR_CONFIG_KEY = (dbId: string) => `freshcards:calendarViewConfig:${dbId}`;
export const CALENDAR_ZOOM_OVERRIDE_KEY = (dbId: string, tf: TimeField) =>
  `freshcards:calendarZoomOverride:${dbId}:${tf}`;

/** Resolve the effective date for an item given the chosen time field. */
export function resolveItemDate(
  item: { createdAt: Date | string; updatedAt: Date | string; properties: Array<{ propertyId: string; value: any }> },
  timeField: TimeField,
  datePropertyId?: string,
): Date | null {
  if (timeField === 'created') return new Date(item.createdAt);
  if (timeField === 'updated') return new Date(item.updatedAt);
  if (timeField === 'completed') {
    const status = item.properties.find(p => p.propertyId === 'status')?.value as any;
    const isDone = status && typeof status === 'object' && (
      status.state === 'completed' ||
      status.state === 'archived' ||
      (status.id && /done|complete|finish/i.test(String(status.id)))
    );
    if (!isDone) return null;
    return new Date(item.updatedAt);
  }
  const propId = datePropertyId || 'date';
  const prop = item.properties.find(p => p.propertyId === propId);
  if (!prop?.value) return null;
  const d = new Date(prop.value);
  return isNaN(d.getTime()) ? null : d;
}

export function fmtDay(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Parse a yyyy-mm-dd string into a Date in local time (not UTC). */
export function parseDay(s: string): Date {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
}

export function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

export function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

/** Per-zoom unit definition: how many days each "step" is. */
export function zoomStepDays(zoom: ZoomLevel): number {
  switch (zoom) {
    case 'day':     return 1;
    case '3-day':  return 3;
    case 'week':   return 7;
    case 'month':  return 30;   // approx
    case 'quarter': return 91;  // approx
    case 'year':   return 365;  // approx
  }
}

/** Build a window of dates relative to today, in the chosen direction. */
export function buildWindow(
  zoom: ZoomLevel,
  anchor: Date,
  direction: 'forward' | 'backward',
  steps: number = 8,
): Date[] {
  const step = zoomStepDays(zoom);
  const a = startOfDay(anchor);
  const out: Date[] = [];
  for (let i = 0; i < steps; i++) {
    const offset = direction === 'backward' ? -i * step : i * step;
    out.push(new Date(a.getTime() + offset * 86400000));
  }
  return out;
}

/** Group consecutive days by year-month. */
export function groupByMonth(days: Date[]): Array<{ year: number; month: number; days: Date[] }> {
  const groups: Array<{ year: number; month: number; days: Date[] }> = [];
  for (const d of days) {
    if (groups.length === 0 || groups[groups.length - 1].year !== d.getFullYear() || groups[groups.length - 1].month !== d.getMonth()) {
      groups.push({ year: d.getFullYear(), month: d.getMonth(), days: [d] });
    } else {
      groups[groups.length - 1].days.push(d);
    }
  }
  return groups;
}

/** Load config from localStorage with smart defaults. */
export function loadCalendarConfig(dbId: string): CalendarViewConfig {
  if (typeof window === 'undefined') return { ...DEFAULT_CALENDAR_CONFIG };
  try {
    const raw = localStorage.getItem(CALENDAR_CONFIG_KEY(dbId));
    if (!raw) return { ...DEFAULT_CALENDAR_CONFIG, zoom: SMART_DEFAULT_ZOOM.due };
    const parsed = JSON.parse(raw) as Partial<CalendarViewConfig>;
    return {
      ...DEFAULT_CALENDAR_CONFIG,
      ...parsed,
      zoom: parsed.zoom ?? SMART_DEFAULT_ZOOM[parsed.timeField ?? 'due'],
    };
  } catch {
    return { ...DEFAULT_CALENDAR_CONFIG };
  }
}

export function saveCalendarConfig(dbId: string, config: CalendarViewConfig): void {
  try {
    localStorage.setItem(CALENDAR_CONFIG_KEY(dbId), JSON.stringify(config));
  } catch {}
}

/** ISO 8601 week number. */
export function isoWeek(d: Date): number {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  return Math.ceil((((date.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
}

/** First day (Monday) of the ISO week containing `d`. */
export function startOfIsoWeek(d: Date): Date {
  const date = new Date(d);
  const day = date.getDay() || 7;
  date.setDate(date.getDate() - (day - 1));
  date.setHours(0, 0, 0, 0);
  return date;
}

/** Last day (Sunday) of the ISO week containing `d`. */
export function endOfIsoWeek(d: Date): Date {
  const start = startOfIsoWeek(d);
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  end.setHours(23, 59, 59, 999);
  return end;
}

const QUICK_FILTER_LABELS: Record<QuickFilter, string> = {
  'all': 'All',
  'overdue': 'Overdue',
  'today': 'Today',
  'this-week': 'This week',
  'no-date': 'No date',
};

export const quickFilterLabel = (f: QuickFilter): string => {
  return QUICK_FILTER_LABELS[f];
};

const STATUS_FILTER_LABELS: Record<StatusFilter, string> = {
  'all': 'All',
  'done': 'Done',
  'wip': 'WIP',
  'backlog': 'Backlog',
  'review': 'Review',
  'archived': 'Archived',
};

export const statusFilterLabel = (f: StatusFilter): string => {
  return STATUS_FILTER_LABELS[f];
};

export const SORT_LABELS: Record<SortKey, string> = {
  'date-asc': 'Date ↑',
  'date-desc': 'Date ↓',
  'title-asc': 'Title A→Z',
  'title-desc': 'Title Z→A',
  'status': 'By status',
  'priority': 'By priority',
};

export function countActiveFilters(c: CalendarViewConfig): number {
  const propCount = Object.values(c.propertyFilter || {}).reduce((acc, opts) => acc + (opts.length > 0 ? 1 : 0), 0);
  return (c.quickFilter !== 'all' ? 1 : 0)
    + (c.statusFilter !== 'all' ? 1 : 0)
    + (c.zoom !== SMART_DEFAULT_ZOOM[c.timeField] ? 1 : 0)
    + (c.sort !== 'date-asc' ? 1 : 0)
    + (c.weekStart !== 'monday' ? 1 : 0)
    + (c.hideWeekends ? 1 : 0)
    + propCount;
}

/** Sort items by the chosen sort key. */
export function sortItems(items: DatabaseItem[], sort: SortKey, timeField: TimeField, datePropertyId?: string): DatabaseItem[] {
  const arr = [...items];
  const getDate = (it: DatabaseItem) => resolveItemDate(it, timeField, datePropertyId)?.getTime() ?? 0;
  switch (sort) {
    case 'date-asc':   arr.sort((a, b) => getDate(a) - getDate(b)); break;
    case 'date-desc':  arr.sort((a, b) => getDate(b) - getDate(a)); break;
    case 'title-asc':  arr.sort((a, b) => a.title.localeCompare(b.title)); break;
    case 'title-desc': arr.sort((a, b) => b.title.localeCompare(a.title)); break;
    case 'status':     arr.sort((a, b) => String(a.properties.find(p => p.propertyId === 'status')?.value?.id || '').localeCompare(String(b.properties.find(p => p.propertyId === 'status')?.value?.id || ''))); break;
    case 'priority':   arr.sort((a, b) => {
      const pa = (a.properties.find(p => p.propertyId === 'priority')?.value as any)?.id || 0;
      const pb = (b.properties.find(p => p.propertyId === 'priority')?.value as any)?.id || 0;
      return Number(pb) - Number(pa);
    }); break;
  }
  return arr;
}

/** Apply Where + Status + Property filters to the items list. */
export function applyFilters(
  items: DatabaseItem[],
  timeField: TimeField,
  quickFilter: QuickFilter,
  statusFilter: StatusFilter,
  datePropertyId?: string,
  propertyFilter?: PropertyFilter,
): DatabaseItem[] {
  const today = startOfDay(new Date());
  const weekStart = startOfIsoWeek(today);
  const weekEnd = endOfIsoWeek(today);

  const getStatus = (item: DatabaseItem) => item.properties.find(p => p.propertyId === 'status')?.value as any;

  return items.filter(item => {
    // Status filter
    if (statusFilter !== 'all') {
      const s = getStatus(item);
      const state = (s && typeof s === 'object' && s.state) ? String(s.state) : String(s?.id || 'backlog');
      const stateLower = state.toLowerCase();
      const matchesStatus =
        (statusFilter === 'done' && (stateLower === 'completed' || stateLower === 'done' || stateLower === 'archived')) ||
        (statusFilter === 'wip' && (stateLower === 'in_progress' || stateLower === 'wip' || stateLower === 'active')) ||
        (statusFilter === 'backlog' && (stateLower === 'backlog' || stateLower === 'todo' || stateLower === 'new')) ||
        (statusFilter === 'review' && stateLower === 'review') ||
        (statusFilter === 'archived' && stateLower === 'archived');
      if (!matchesStatus) return false;
    }

    // Where filter
    if (quickFilter !== 'all') {
      const itemDate = resolveItemDate(item, timeField, datePropertyId);
      if (quickFilter === 'no-date') {
        if (itemDate != null) return false;
      } else {
        if (itemDate == null) return false;
        const d = startOfDay(itemDate);
        if (quickFilter === 'today' && !isSameDay(d, today)) return false;
        if (quickFilter === 'this-week' && !(d >= weekStart && d <= weekEnd)) return false;
        if (quickFilter === 'overdue' && !(d < today)) return false;
      }
    }

    // Property filter — AND across properties
    if (propertyFilter) {
      for (const [propId, selectedIds] of Object.entries(propertyFilter)) {
        if (!selectedIds || selectedIds.length === 0) continue;
        const propVal = item.properties.find(p => p.propertyId === propId)?.value;
        if (propVal == null) return false;
        if (Array.isArray(propVal)) {
          const has = selectedIds.some(id => propVal.map(String).includes(String(id)));
          if (!has) return false;
        } else {
          const vId = (typeof propVal === 'object' && (propVal as any).id) ? String((propVal as any).id) : String(propVal);
          if (!selectedIds.map(String).includes(vId)) return false;
        }
      }
    }

    return true;
  });
}

/**
 * Discover ALL properties that exist in the database.
 *
 * Universal — works for any property the user might add:
 * - Schema-declared properties come first (with their declared type + options)
 * - Item-observed properties fill the gap (with type inferred from observed values)
 * - Result: filtering works whether the schema is declared or not
 */
export interface DiscoveredProperty {
  id: string;
  name: string;
  type: 'select' | 'multi_select' | 'status' | 'person' | 'text' | 'number' | 'date' | 'checkbox' | 'unknown';
  options: Array<{ id: string; name: string; color?: string }>;
  source: 'schema' | 'observed';
}

export function discoverProperties(
  schemaProps: PropertyDefinition[] | undefined,
  items: DatabaseItem[],
): DiscoveredProperty[] {
  // First pass: build a property map from items, gathering ALL options observed
  const fromItems = new Map<string, DiscoveredProperty>();
  for (const it of items) {
    for (const pv of it.properties) {
      if (pv.propertyId === 'title' || pv.propertyId === 'cover') continue;
      const v = pv.value as any;
      if (v == null) continue;
      let prop = fromItems.get(pv.propertyId);
      if (!prop) {
        prop = {
          id: pv.propertyId,
          name: pv.propertyId.charAt(0).toUpperCase() + pv.propertyId.slice(1),
          type: 'unknown',
          options: [],
          source: 'observed',
        };
        fromItems.set(pv.propertyId, prop);
      }
      // Type inference from first non-null value
      if (prop.type === 'unknown') {
        if (Array.isArray(v)) prop.type = 'multi_select';
        else if (typeof v === 'object' && v && (v.id !== undefined || v.name !== undefined)) prop.type = 'select';
        else if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}/.test(v)) prop.type = 'date';
        else if (typeof v === 'string') prop.type = 'text';
        else if (typeof v === 'number') prop.type = 'number';
        else if (typeof v === 'boolean') prop.type = 'checkbox';
      }
      // Gather options
      const seenOpts = new Set(prop.options.map(o => o.id));
      if (Array.isArray(v)) {
        for (const x of v) {
          const id = typeof x === 'object' ? String(x?.id ?? '') : String(x);
          const name = typeof x === 'object' ? String(x?.name ?? id) : id;
          if (id && !seenOpts.has(id)) { seenOpts.add(id); prop.options.push({ id, name, color: typeof x === 'object' ? x.color : undefined }); }
        }
      } else if (typeof v === 'object' && v) {
        const id = String(v.id ?? '');
        const name = String(v.name ?? id);
        if (id && !seenOpts.has(id)) { seenOpts.add(id); prop.options.push({ id, name, color: v.color }); }
      } else if (typeof v === 'string' && prop.type === 'text') {
        if (!seenOpts.has(v)) { seenOpts.add(v); prop.options.push({ id: v, name: v }); }
      } else if (typeof v === 'boolean') {
        // checkbox: Yes / No already added on first encounter
      }
    }
  }
  // Second pass: schema-declared properties (override observed types where defined)
  const out: DiscoveredProperty[] = [];
  const seen = new Set<string>();
  for (const p of (schemaProps || [])) {
    if (seen.has(p.id)) continue;
    seen.add(p.id);
    const observed = fromItems.get(p.id);
    const schemaOpts = ((p.options || p.statusOptions || []) as any[]).map(o => ({ id: String(o.id), name: String(o.name), color: o.color }));
    // Merge schema + observed options (schema first, then observed-only)
    const seenO = new Set(schemaOpts.map(o => o.id));
    const merged = [...schemaOpts];
    if (observed) {
      for (const o of observed.options) {
        if (!seenO.has(o.id)) { seenO.add(o.id); merged.push(o); }
      }
    }
    out.push({
      id: p.id,
      name: p.name || p.id,
      type: (['select', 'multi_select', 'status', 'person', 'text', 'number', 'date', 'checkbox'].includes(p.type) ? p.type : (observed?.type || 'unknown')) as any,
      options: merged,
      source: 'schema',
    });
  }
  // Third pass: any item-observed properties not in schema
  for (const [id, prop] of fromItems) {
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(prop);
  }
  return out;
}

// ─── Relative date helpers (for quick-jump) ─────────────────────────────
export const RELATIVE_DATES: Array<{ label: string; get: () => Date }> = [
  { label: 'Today', get: () => startOfDay(new Date()) },
  { label: 'Tomorrow', get: () => { const d = new Date(); d.setDate(d.getDate() + 1); return startOfDay(d); } },
  { label: 'Yesterday', get: () => { const d = new Date(); d.setDate(d.getDate() - 1); return startOfDay(d); } },
  { label: 'Next Monday', get: () => {
    const d = new Date();
    const day = d.getDay() || 7;
    const daysToMon = day === 1 ? 7 : 8 - day;
    d.setDate(d.getDate() + daysToMon);
    return startOfDay(d);
  } },
  { label: '2 weeks from now', get: () => { const d = new Date(); d.setDate(d.getDate() + 14); return startOfDay(d); } },
  { label: '1st of next month', get: () => { const d = new Date(); d.setMonth(d.getMonth() + 1, 1); return startOfDay(d); } },
  { label: '1st of last month', get: () => { const d = new Date(); d.setMonth(d.getMonth() - 1, 1); return startOfDay(d); } },
];

// DatabaseItem shape (mirror of src/types/models/Database)
export type DatabaseItem = {
  id: string;
  title: string;
  createdAt: Date | string;
  updatedAt: Date | string;
  properties: Array<{ propertyId: string; value: any }>;
};

/** Re-declared here to avoid cross-module type imports in this pure module. */
export type PropertyType = 'title' | 'text' | 'number' | 'select' | 'multi_select' | 'date' | 'checkbox' | 'url' | 'files' | 'relation' | 'formula' | 'status' | 'person' | 'phone' | 'email' | 'created_time' | 'last_edited_time';

export interface PropertyDefinition {
  id: string;
  name?: string;
  type: PropertyType;
  visible?: boolean;
  position?: number;
  options?: Array<{ id: string; name: string; color?: string }>;
  statusOptions?: Array<{ id: string; name: string; color?: string; state?: string }>;
  numberFormat?: any;
  dateFormat?: string;
  defaultValue?: any;
  formula?: string;
  readOnly?: boolean;
}
